import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ExportExcelService } from '../../../../shared/excel-service/export-excel.service';
import * as moment from 'moment';

// Interface para tipagem dos dados
interface DealerFaturamento {
  regiao: string;
  name: string;
  dias: { [key: string]: number };
  total: number;
}

@Component({
  selector: 'app-faturamento-diario',
  templateUrl: './faturamento-diario.component.html',
  styleUrls: ['./faturamento-diario.component.scss']
})
export class FaturamentoDiarioComponent implements OnChanges {

  @Input() dadosGrid: any = null; // Pode ser array ou objeto com dataInicial, dataFinal e dados
  @Input() loading: boolean = false;
  @Input() tipoRelatorio: 'regional' | 'faturamentoDiario' = 'faturamentoDiario';
  @Input() dataInicial: Date; // Fallback para data inicial
  @Input() dataFinal: Date; // Fallback para data final

  dados: any[] = [];
  totalizador: any = {};
  diasDoMes: any[] = []; // Mudança para array de objetos com data e dia
  
  // Estado para controlar a expansão/colapso dos grupos regionais
  private regionCollapsedState = new Map<string, boolean>();
  
  // Estado para controlar se a coluna dealer está fixada
  isDealerColumnFixed: boolean = false;
  
  // Propriedades computadas para evitar re-renderização
  private _colunasFixasAtivasCache: any[] = [];
  private _colunasDiasCache: any[] = [];

  // Configuração das colunas fixas - aqui você pode ajustar facilmente
  // Para adicionar/remover colunas, basta editar este array
  // Para ajustar larguras, altere o valor 'width'
  // Para mudar cabeçalhos, altere o valor 'header'
  // IMPORTANTE: A coluna 'total' será renderizada separadamente no final da grid
  private colunasFixasConfig = [
    { field: 'name', header: 'Dealer', width: 350 }  // Coluna do nome do dealer
    // Exemplo de como adicionar mais colunas:
    // { field: 'regiao', header: 'Região', width: 200 },
    // { field: 'outroCampo', header: 'Outro Campo', width: 150 }
  ];

  constructor(
    private translate: TranslateService,
    private excelService: ExportExcelService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dadosGrid'] && this.dadosGrid) {
      this.processarDados();
    }
    // Calcular dias do período quando os dados mudarem ou quando as datas de fallback mudarem
    if (changes['dadosGrid'] || changes['dataInicial'] || changes['dataFinal']) {
      this.calcularDiasDoPeriodo();
    }
  }

  private calcularDiasDoPeriodo() {
    let dataInicial: moment.Moment;
    let dataFinal: moment.Moment;

    // Tentar obter as datas da API primeiro
    if (this.dadosGrid && typeof this.dadosGrid === 'object' && this.dadosGrid.dataInicial && this.dadosGrid.dataFinal) {
      dataInicial = moment(this.dadosGrid.dataInicial);
      dataFinal = moment(this.dadosGrid.dataFinal);
    } else if (this.dataInicial && this.dataFinal) {
      // Fallback para as datas do input
      dataInicial = moment(this.dataInicial);
      dataFinal = moment(this.dataFinal);
    } else {
      console.warn('Nenhuma data fornecida para cálculo do período');
      this.diasDoMes = [];
      this.invalidarCaches();
      return;
    }

    // Verificar se as datas são válidas
    if (!dataInicial.isValid() || !dataFinal.isValid()) {
      console.warn('Datas inválidas fornecidas para cálculo do período');
      this.diasDoMes = [];
      this.invalidarCaches();
      return;
    }

    // Verificar se a data final é maior ou igual à inicial
    if (dataFinal.isBefore(dataInicial)) {
      console.warn('Data final é anterior à data inicial');
      this.diasDoMes = [];
      this.invalidarCaches();
      return;
    }

    const dias: any[] = [];
    const dataAtual = dataInicial.clone();

    // Gerar array de dias do período
    while (dataAtual.isSameOrBefore(dataFinal)) {
      dias.push({
        dia: dataAtual.date(),
        data: dataAtual.clone(),
        header: dataAtual.format('DD/MM'),
        field: `dia${dataAtual.date()}`, // Mantém o formato dia1, dia2, etc. para compatibilidade com a API
        width: 80
      });
      dataAtual.add(1, 'day');
    }

    this.diasDoMes = dias;
    this.invalidarCaches();
  }



  private processarDados() {
    if (!this.dadosGrid) {
      this.limparDadosGrid();
      return;
    }

    let dadosParaProcessar: any[] = [];

    // Verificar se dadosGrid é um objeto com estrutura da API ou array direto
    if (typeof this.dadosGrid === 'object' && this.dadosGrid.dados && Array.isArray(this.dadosGrid.dados)) {
      // Estrutura da API: { dataInicial, dataFinal, dados: [...] }
      dadosParaProcessar = this.dadosGrid.dados;
    } else if (Array.isArray(this.dadosGrid)) {
      // Estrutura direta: array de dealers
      dadosParaProcessar = this.dadosGrid;
    } else {
      console.warn('Formato de dados não reconhecido:', this.dadosGrid);
      this.limparDadosGrid();
      return;
    }

    if (dadosParaProcessar.length > 0) {
      const dadosProcessados = this.processarDadosAPI(dadosParaProcessar);
      const dadosSemTotal = dadosProcessados.filter(d => d.name !== 'TOTAL');

      this.dados = this.inserirTotalizadoresPorRegiao(dadosSemTotal);
      this.calcularTotalizador();
      this.invalidarCaches();
    } else {
      this.limparDadosGrid();
    }
  }

  private processarDadosAPI(dadosAPI: any[]): any[] {
    // Verificar se os dados estão no formato correto
    if (dadosAPI.length > 0 && dadosAPI[0].dias) {
      console.log('Dados já estão no formato correto');
      return dadosAPI;
    }
    
    console.warn('Dados não estão no formato esperado (sem campo dias)');
    return dadosAPI;
  }

  private limparDadosGrid() {
    this.dados = [];
    this.totalizador = {};
    this.invalidarCaches();
  }

  private invalidarCaches() {
    this._colunasFixasAtivasCache = [];
    this._colunasDiasCache = [];
  }

  get colunasFixasAtivas() {
    if (this._colunasFixasAtivasCache.length === 0) {
      this.atualizarColunasFixasAtivas();
    }
    return this._colunasFixasAtivasCache;
  }

  private atualizarColunasFixasAtivas() {
    // Usar a configuração das colunas fixas (sem a coluna total)
    this._colunasFixasAtivasCache = this.colunasFixasConfig.map(col => ({
      field: col.field,
      header: col.field === 'name' 
        ? this.translate.instant('relatorioVeiculos.veiculoRegional.colunas.dealer')
        : col.header,
      width: col.width
    }));
  }

  get colunasDias() {
    if (this._colunasDiasCache.length === 0) {
      this.atualizarColunasDias();
    }
    return this._colunasDiasCache;
  }

  private atualizarColunasDias() {
    this._colunasDiasCache = this.diasDoMes.map(diaObj => ({
      field: diaObj.field,
      header: diaObj.header,
      width: diaObj.width
    }));
  }

  inserirTotalizadoresPorRegiao(dados: any[]): any[] {
    const agrupados: { [regiao: string]: any[] } = {};
    const resultado: any[] = [];

    for (const dealer of dados) {
      if (!dealer.regiao) {
        resultado.push(dealer);
        continue;
      }

      agrupados[dealer.regiao] = agrupados[dealer.regiao] || [];
      agrupados[dealer.regiao].push(dealer);
    }

    for (const regiao in agrupados) {
      const dealers = agrupados[regiao];
      resultado.push(...dealers);

      const subtotal = this.calcularSubtotalRegiao(regiao, dealers);
      resultado.push(subtotal);
    }

    return resultado;
  }

  private calcularSubtotalRegiao(regiao: string, dealers: any[]): any {
    const subtotal: any = {
      name: 'Total - ' + regiao,
      regiao: regiao,
      isTotalRegiao: true,
      dias: {}
    };

    // Adicionar dados dos dias
    this.diasDoMes.forEach(diaObj => {
      const campoDia = diaObj.field;
      subtotal.dias[campoDia] = dealers.reduce((total, dealer) => {
        return total + (dealer.dias?.[campoDia] || 0);
      }, 0);
    });

    // Adicionar totais
    subtotal.total = dealers.reduce((total, dealer) => {
      return total + (dealer.total || 0);
    }, 0);

    // Adicionar total cancelados/devolvidos
    subtotal.totalCancelDevol = dealers.reduce((total, dealer) => {
      return total + (dealer.totalCancelDevol || 0);
    }, 0);

    // Adicionar total varejo
    subtotal.totalVarejo = dealers.reduce((total, dealer) => {
      return total + (dealer.totalVarejo || 0);
    }, 0);

    return subtotal;
  }

  calcularTotalizador() {
    const dealers = this.dados.filter(d => !d.isTotalRegiao);
  
    const total: any = {};
  
    // Calcular totais para cada dia
    this.diasDoMes.forEach(diaObj => {
      const campoDia = diaObj.field;
      total[campoDia] = dealers.reduce((sum, dealer) => {
        return sum + (dealer.dias?.[campoDia] || 0);
      }, 0);
    });

    // Calcular total faturamento
    total.total = dealers.reduce((sum, dealer) => {
      return sum + (dealer.total || 0);
    }, 0);

    // Calcular total cancelados/devolvidos
    total.totalCancelDevol = dealers.reduce((sum, dealer) => {
      return sum + (dealer.totalCancelDevol || 0);
    }, 0);

    // Calcular total varejo
    total.totalVarejo = dealers.reduce((sum, dealer) => {
      return sum + (dealer.totalVarejo || 0);
    }, 0);

    this.totalizador = total;
  }

  getValorTotalizador(campo: string): number {
    return this.totalizador?.[campo] ?? 0;
  }

  getDiaData(dealer: any, diaObj: any) {
    const campoDia = diaObj.field;
    return dealer.dias?.[campoDia] || 0;
  }

  // TrackBy functions para otimizar performance
  trackByDia(index: number, diaObj: any): number {
    return diaObj.dia;
  }

  trackByColIndex(index: number, col: any): string {
    return col.field;
  }

  trackByDealerName(index: number, dealer: any): string {
    return dealer.name || index.toString();
  }

  // Método para gerar classes CSS das linhas
  getRowClasses(dealer: any): any {
    return {
      'total-regiao': dealer.isTotalRegiao,
      'dealer-row': !dealer.isTotalRegiao
    };
  }

  // Métodos para controlar expansão/colapso dos grupos regionais
  toggleRegionCollapse(regiao: string): void {
    const currentState = this.regionCollapsedState.get(regiao) || false;
    this.regionCollapsedState.set(regiao, !currentState);
  }

  isRegionCollapsed(regiao: string): boolean {
    return this.regionCollapsedState.get(regiao) || false;
  }

  shouldShowDealer(dealer: any, index: number): boolean {
    if (dealer.isTotalRegiao) {
      return true; // Sempre mostrar totalizadores regionais
    }

    // Para dealers normais, verificar se a região está expandida
    const regiao = dealer.regiao;
    if (regiao) {
      return !this.isRegionCollapsed(regiao);
    }

    // Se não tem região definida, mostrar por padrão
    return true;
  }

  getRegionIconClass(regiao: string): string {
    return this.isRegionCollapsed(regiao) ? 'pi pi-chevron-right' : 'pi pi-chevron-up';
  }

  getHeaderIconClass(): string {
    return this.areAllRegionsExpanded() ? 'pi pi-eye' : 'pi pi-eye-slash';
  }

  expandAllRegions(): void {
    const regioes = this.getUniqueRegions();
    regioes.forEach(regiao => this.regionCollapsedState.set(regiao, false));
  }

  collapseAllRegions(): void {
    const regioes = this.getUniqueRegions();
    regioes.forEach(regiao => this.regionCollapsedState.set(regiao, true));
  }

  toggleAllRegions(): void {
    if (this.areAllRegionsExpanded()) {
      this.collapseAllRegions();
    } else {
      this.expandAllRegions();
    }
  }
  
  areAllRegionsExpanded(): boolean {
    const regioes = this.getUniqueRegions();
    if (regioes.length === 0) return false;
    
    return regioes.every(regiao => !this.isRegionCollapsed(regiao));
  }

  private getUniqueRegions(): string[] {
    const regioes = new Set<string>();
    this.dados.forEach(dealer => {
      if (dealer.regiao) {
        regioes.add(dealer.regiao);
      }
    });
    return Array.from(regioes);
  }

  // Métodos para alternar fixação da coluna dealer
  toggleDealerColumnFixed(): void {
    this.isDealerColumnFixed = !this.isDealerColumnFixed;
  }

  // Método para obter classe CSS da coluna dealer
  getDealerColumnClass(): string {
    return this.isDealerColumnFixed ? 'fixed-dealer-column' : '';
  }

  // Método para obter ícone do botão de fixação
  getDealerColumnIconClass(): string {
    return this.isDealerColumnFixed ? 'pi pi-lock' : 'pi pi-lock-open';
  }
}